import { cn } from '@/helpers/ui-utils';
import { Slot } from '@radix-ui/react-slot';
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationLinkProps,
  Pagination as PaginationRoot,
} from './pagination';

const Pagination = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>((props, ref) => {
  return (
    <PaginationRoot>
      <PaginationContent ref={ref} {...props} />
    </PaginationRoot>
  );
});

interface NavItemProps extends React.ComponentProps<'a'> {
  /**
   * The type of navigation item, determines which icon to display.
   * - `prev` - Previous page navigation with left arrow
   * - `next` - Next page navigation with right arrow
   * @default 'prev'
   */
  type?: 'prev' | 'next';

  /**
   * Render as child component using Radix Slot. Allows composition with other elements while maintaining navigation styling.
   * @default false
   */
  asChild?: boolean;

  /**
   * Whether the navigation item is disabled. Prevents interaction and applies disabled styling.
   * @default false
   */
  disabled?: boolean;

  /**
   * The URL or path to navigate to when the navigation item is clicked.
   */
  href: string;
}

export const NavItem: React.FC<NavItemProps> = ({
  type = 'prev',
  asChild,
  ...props
}) => {
  const Comp = asChild ? Slot : PaginationLink;

  const label: Record<'prev' | 'next', string> = {
    prev: 'Go to previous page',
    next: 'Go to next page',
  };

  const iconClasses = [
    'transition-stroke',
    'h-6',
    'w-6',
    'stroke-[#737373]',
    'duration-300',
    'ease-in-out',
    'group-hover/nav-item:stroke-primary',
  ];

  const icon: Record<'prev' | 'next', React.ReactNode> = {
    prev: <ChevronLeft className={cn(iconClasses)} />,
    next: <ChevronRight className={cn(iconClasses)} />,
  };

  return (
    <PaginationItem>
      <Comp
        className={cn(['group/nav-item', 'px-2'])}
        title={label[type]}
        aria-label={label[type]}
        {...props}
      >
        {icon[type]}
      </Comp>
    </PaginationItem>
  );
};

export const PageLink = (props: PaginationLinkProps) => (
  <PaginationItem>
    <PaginationLink {...props} />
  </PaginationItem>
);

export const Ellipsis = () => (
  <PaginationItem>
    <PaginationEllipsis />
  </PaginationItem>
);

export default Pagination;
